<script lang="ts">
    import Fa from 'svelte-fa'
    import {
        faCalculator,
        faCreditCard,
        faEdit,
        faInfinity,
        faMoneyBill,
        faPlusCircle,
        faUser,
        faWindowClose,
        faPrint,
        faTrash,
    } from '@fortawesome/free-solid-svg-icons'
    import { onMount } from 'svelte/internal'
    import Content from '../../_common/content.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import DateSelect from '../../_common/date-select.svelte'
    import Popover from '../../_common/popover.svelte'
    import { GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import Financial_4BillsForm from './financial-4-bills-form.svelte'
    import type { BillEntity } from '../../../modules/financialApi/bills/bill'
    import dayjs from 'dayjs'
    import _isToday from 'dayjs/plugin/isToday'
    import { EntryTypeEnum } from '../../../modules/financialApi/entries/entry.enum'
    import Financial_4BillsFormPayment from './financial-4-bills-form-payment.svelte'
    import Financial_4BillsRecurring from './financial-4-bills-recurring.svelte'
    import PaymentsPurchaseShow from '../../payments/payments-purchase-show.svelte'
    import PosForm from '../../pos/pos-form.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import { FormTemplateTypeEnum } from '../../../modules/toolsApi/forms/form-templates/form-template-item-type.enum'
    import { printFormV2 } from '../../customer-service-v2/forms/form-v2-print.helper'

    dayjs.extend(_isToday)

    let show = false
    let entities: BillEntity[] = []
    let total = 0

    let dates: { from: Date; to: Date } = null
    function showDate() {
        dates = window.api.utils.dates.currentMonth()
    }

    onMount(async () => {
        await loadEntities()
    })

    function dateChanged(event) {
        dates = event.detail
        loadEntities()
    }

    async function loadEntities() {
        try {
            show = true
            total = 0
            const dt = {
                from: filterStatus === 'overdue' ? undefined : dates?.from || undefined,
                to: dates?.to || undefined,
                status: filterStatus || undefined,
                creditDebit: 'credit',
            }
            entities = await window.api.financials.bills.getAll(dt)
            entities = await window.api.utils.utils.assignPersonsFromObject(entities)
            total = entities.reduce((total, item) => {
                total += item.paidValue || item.value
                return total
            }, 0)
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function evaluateBadge(item: BillEntity): { label: string; class: string } {
        if (item.status === 100) {
            return {
                label: 'recebido',
                class: GridBadgeClassEnum.badge_success,
            }
        }
        const isToday = dayjs(item.dueAt).isToday()
        if (isToday) {
            return {
                label: 'vence hoje',
                class: GridBadgeClassEnum.badge_alert,
            }
        }
        if (dayjs(item.dueAt).diff(new Date(), 'day') >= 0) {
            return {
                label: 'a receber',
                class: GridBadgeClassEnum.badge_default,
            }
        }
        return {
            label: 'atrasado',
            class: GridBadgeClassEnum.badge_danger,
        }
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Contas a Receber'
        grid.subtitle = ''
        grid.fontSize = '15px'
        // seleção em massa só para quem pode excluir (mesma permissão do "Excluir" individual)
        const canRemove = window.store.token.permissions.financial.entries.write
        if (canRemove) {
            const btnRemoveSelected = new GridButton({ callback: removeSelected, icon: faTrash, label: 'Excluir selecionados' })
            grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.checkbox, bulkActions: [btnRemoveSelected] }))
        }
        grid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Origem', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Vencimento', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Recebido em', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Descrição', type: GridColTypeEnum.string, maxLength: 45 }))
        grid.cols.push(new GridCol({ content: 'Pagador', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'A receber', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: 'Recebido', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnPerson = new GridButton({
                callback: () => window.person('show', item.person?.personId),
                icon: faUser,
                label: '',
                disabled: item.personId === null,
            })

            let btnOpen = null
            if (item.source === 'zandupay') {
                btnOpen = new GridButton({
                    callback: () => openPurchaseShow(item.groupId),
                    icon: faCreditCard,
                    label: 'ZanduPay',
                })
            } else if (item.source === 'pos') {
                btnOpen = new GridButton({
                    callback: () => openPDV(item.invoiceId),
                    icon: faCreditCard,
                    label: 'PDV',
                })
            } else {
                btnOpen = new GridButton({
                    callback: () => goShowBill(item.billId),
                    icon: faEdit,
                    label: 'Editar',
                })
            }

            let btnDoc = null
            btnDoc = new GridButton({
                callback: () => openDocument(item.billId, item.personId || item.person?.personId),
                icon: faPrint,
            })
            let btnPay = null
            if (item.source !== 'zandupay') {
                if (item.status === 95) {
                    btnPay = new GridButton({
                        callback: () => goShowPayment(item.billId),
                        icon: faMoneyBill,
                        label: 'Receber',
                        disabled: item.source === 'zandupay',
                    })
                } else {
                    btnPay = new GridButton({
                        callback: () => voidPayment(item.billId),
                        icon: faWindowClose,
                        label: 'Estornar',
                        disabled: item.source === 'zandupay',
                    })
                }
            }

            let description = item.description
            if (item.installmentTotal > 1) {
                description = '(' + item.installmentNumber + '/' + item.installmentTotal + ') ' + description
            }
            let line: GridLine = new GridLine()
            if (canRemove) line.slots.push({ content: item })
            line.slots.push({ content: new GridBadge(evaluateBadge(item)) })
            if (item.source === 'zandupay') {
                line.slots.push({
                    content: new GridBadge({ class: GridBadgeClassEnum.badge_purple, label: 'zandupay' }),
                })
            } else if (item.source === 'pos') {
                line.slots.push({ content: new GridBadge({ class: GridBadgeClassEnum.badge_purple, label: 'pdv' }) })
            } else if (item.source === 'recurring') {
                line.slots.push({
                    content: new GridBadge({ class: GridBadgeClassEnum.badge_default, label: 'recorrente' }),
                })
            } else {
                line.slots.push({
                    content: new GridBadge({ class: GridBadgeClassEnum.badge_default, label: 'digitação' }),
                })
            }
            let btns = []
            btns.push(btnPay)
            btns.push(btnOpen)
            if (btnDoc) btns.push(btnDoc)
            btns.push(btnPerson)
            line.slots.push({ content: item.dueAt })
            line.slots.push({ content: item.paidAt })
            line.slots.push({ content: description })
            line.slots.push({ content: item.person?.name || '-' })
            line.slots.push({ content: item.value })
            line.slots.push({ content: item.status >= 100 ? item.paidValue : null })
            line.slots.push({ content: btns })
            grid.lines.push(line)
        }
    }

    function openDocument(billId: string, personId: string) {
        if (window.configs?.documentVersionLatest) {
            window.popover({
                act: 'customer-service-v2',
                data: {
                    view: false,
                    personId,
                    billId,
                },
                callback: checkPrintV2,
            })
        } else {
            window.popover({
                act: 'customer-service',
                data: {
                    view: false,
                    personId,
                    billId,
                    types: [FormTemplateTypeEnum.recibo, FormTemplateTypeEnum.contrato],
                },
                callback: printDocument,
            })
        }
    }

    function printDocument(data) {
        if (!data.formDataId) return
        if (!confirm('Deseja imprimir o documento?')) return
        setTimeout(() => {
            window.popover({
                act: 'customer-service',
                data: {
                    view: true,
                    formDataId: data.formDataId,
                },
            })
        }, 1000)
    }

    async function checkPrintV2(data) {
        if (!data.formV2FormId) return
        if (!confirm('Deseja imprimir o documento?')) return
        try {
            show = true
            await printFormV2(data.formV2FormId)
        } finally {
            show = false
        }
    }

    let billId = null
    let showBill = false
    function goShowBill(id: string) {
        billId = id
        showBill = true
    }

    async function voidPayment(id: string) {
        if (!confirm('Deseja mesmo estornar o recebimento desta conta?')) return
        await window.api.financials.bills.void(id)
        await loadEntities()
    }

    // ação em massa da coluna de seleção do grid — recebe as contas marcadas (visíveis) no grid
    async function removeSelected(items: BillEntity[]) {
        // ZanduPay e PDV são mantidos pela integração e não podem ser excluídos por aqui (nem individualmente)
        const removables = items.filter((item) => item.source !== 'zandupay' && item.source !== 'pos')
        const ignored = items.length - removables.length
        if (removables.length === 0) {
            window.alert('As contas selecionadas vêm do ZanduPay ou do PDV e não podem ser excluídas por aqui.')
            return
        }
        const received = removables.filter((item) => item.status === 100).length
        let message = `Deseja mesmo excluir ${removables.length} conta(s) a receber?`
        if (received > 0) {
            message += `\n\n${received} delas já foram recebidas. Se o recebimento foi lançado em uma conta financeira, esse lançamento também será excluído da Movimentação e dos relatórios.`
        }
        if (ignored > 0) {
            message += `\n\n${ignored} conta(s) do ZanduPay ou do PDV não podem ser excluídas por aqui e serão ignoradas.`
        }
        message += '\n\nEsta ação não pode ser desfeita.'
        if (!confirm(message)) return

        let failed = 0
        try {
            show = true
            for (const item of removables) {
                try {
                    await window.api.financials.bills.remove(item.billId)
                } catch (error) {
                    failed++
                }
            }
        } finally {
            show = false
        }
        if (failed > 0) {
            window.error(`${failed} de ${removables.length} conta(s) não puderam ser excluídas. Tente novamente.`)
        }
        await loadEntities()
    }

    let showPayment = false
    function goShowPayment(id: string) {
        billId = id
        showPayment = true
    }

    function goNewBill() {
        billId = null
        showBill = true
    }

    function goCloseBill(update: boolean) {
        showBill = false
        showPayment = false
        billId = null
        if (update) loadEntities()
    }

    let filterStatus: 'recent' | 'overdue' | 'date' = 'recent'
    let filterCurrent = 'recent'
    function loadFromFilter(type) {
        filterCurrent = type
        if (type === 'recent') {
            dates = null
            filterStatus = 'recent'
        }
        if (type === 'overdue') {
            dates = null
            filterStatus = 'overdue'
        }
        if (type === 'date') {
            showDate()
            filterStatus = null
        }
        loadEntities()
    }

    let recurringBillId = null
    let showRecurring = false
    function goCloseRecurring() {
        showRecurring = false
        recurringBillId = null
    }

    function goNewRecurringBill() {
        showRecurring = true
    }

    let purchaseId: string = null
    function openPurchaseShow(id: string) {
        purchaseId = id
    }

    async function closePurchaseShow() {
        purchaseId = null
        await loadEntities()
    }

    let invoiceId: string = null
    function openPDV(id: string) {
        invoiceId = id
    }

    async function closePDV() {
        show = true
        invoiceId = null

        setTimeout(async () => {
            await loadEntities()
            window.alert('Aviso: alterações feitas no PDV podem levar alguns instantes até refletirem no Contas a Receber.')
        }, 3000)
    }
</script>

{#if purchaseId}
    <Popover title="Cobrança" width="1100px" height="80vh" on:close={closePurchaseShow}>
        <PaymentsPurchaseShow {purchaseId} on:close={closePurchaseShow} />
    </Popover>
{/if}

{#if invoiceId}
    <Popover title="Venda" width="1300px" height="90vh" on:close={() => (invoiceId = null)}>
        <PosForm {invoiceId} on:change={closePDV} on:cancel={() => (invoiceId = null)} />
    </Popover>
{/if}

{#if showBill}
    <Popover title="Novo recebível" width="970px" on:close={() => goCloseBill(true)}>
        <Financial_4BillsForm {billId} creditDebit={EntryTypeEnum.credit} on:change={() => goCloseBill(true)}></Financial_4BillsForm>
    </Popover>
{/if}

{#if showPayment}
    <Popover title="Receber" width="840px" on:close={() => goCloseBill(true)}>
        <Financial_4BillsFormPayment {billId} on:change={() => goCloseBill(true)}></Financial_4BillsFormPayment>
    </Popover>
{/if}

{#if showRecurring}
    <Popover title="Recebimentos Recorrentes" width="1300px" height="90vh" on:close={() => goCloseRecurring()}>
        <Financial_4BillsRecurring creditDebit={EntryTypeEnum.credit} on:close={() => goCloseRecurring()}></Financial_4BillsRecurring>
    </Popover>
{/if}

<Content>
    <Zact>
        <div class="btn-group" role="group">
            <button type="button" on:click={() => loadFromFilter('recent')} class="btn btn-secondary" class:btn-primary={filterCurrent === 'recent'}
                >Recentes</button
            >
            <button type="button" on:click={() => loadFromFilter('overdue')} class="btn btn-secondary" class:btn-primary={filterCurrent === 'overdue'}
                >Atrasadas</button
            >
            <button type="button" on:click={() => loadFromFilter('date')} class="btn btn-secondary" class:btn-primary={filterCurrent === 'date'}
                >Por data</button
            >
        </div>
        {#if dates}
            <DateSelect shortcuts={false} startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        {/if}
        <div class="spacer h-mobile" />

        <button class="btn btn-secondary" on:click={goNewRecurringBill}><Fa icon={faInfinity} /> Recebimentos recorrentes</button>
        <button class="btn btn-primary" on:click={goNewBill}><Fa icon={faPlusCircle} /> Novo recebível</button>
    </Zact>
    <Zgrid {grid} class="mb-5 h-space" />
    <LoadSpinner {show} />
</Content>

<style>
</style>
